import {
  Box,
  Grid2,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../components/common/form/CustomTypography";
import { Task } from "../../../models/TaskTracker/Task";
import { TaskAssignee } from "../../../models/TaskTracker/TaskAssignee";
import { OrderProducts } from "../../../models/TaskTracker/OrderProducts";
import { formatDate } from "../../components/common/functions/formatDate";

const ViewTaskPopup = ({
  open,
  onClose,
  data,
}: {
  open: boolean;
  onClose: () => void;
  data: Task;
}) => {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      maxWidth="md"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          px: 4,
          py: 2,
          height: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 0,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <CustomTypography variant="heading4" color="primary">
            Task details
          </CustomTypography>
          <Chip
            label={
              data?.status === 1
                ? "TO DO"
                : data?.status === 2
                ? "IN PROGRESS"
                : data?.status === 3
                ? "BLOCKED"
                : "COMPLETED"
            }
            sx={{
              backgroundColor:
                data?.status === 1
                  ? theme?.palette?.grey[500]
                  : data?.status === 2
                  ? theme?.palette?.info?.light
                  : data?.status === 3
                  ? theme?.palette?.error?.light
                  : theme?.palette?.success?.light,
              color: theme?.palette?.customColors?.white,
              fontSize: "12px",
              borderRadius: "4px",
              height: "25px",
              minWidth: "100px",
              fontWeight: 600,
            }}
          />
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ borderBottomWidth: "1px", mx: 2, pt: 2 }} />
      <DialogContent sx={{ scrollbarWidth: "none" }}>
        <CustomTypography variant="heading5" style={{ marginBottom: 20 }}>
          {data?.title}
        </CustomTypography>

        <Grid2 container spacing={2} mt={3}>
          <Grid2 size={{ xs: 6 }}>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.BlueLightTextColor}
              style={{ marginBottom: "0.5rem" }}
            >
              Created by :
            </CustomTypography>
            <CustomTypography variant="heading6">
              {data?.owner_name || "N/A"}
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.BlueLightTextColor}
              style={{ marginBottom: "0.5rem" }}
            >
              Created time :
            </CustomTypography>
            <CustomTypography variant="heading6">
              {formatDate(data?.created_date)}
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.BlueLightTextColor}
              style={{ marginBottom: "0.5rem" }}
            >
              Due date:
            </CustomTypography>
            <CustomTypography variant="heading6">
              {formatDate(data?.due_date)}
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.BlueLightTextColor}
              style={{ marginBottom: "0.5rem" }}
            >
              Assignee:
            </CustomTypography>
            <CustomTypography variant="heading6">
              {data?.assignees
                ?.map((assignee: TaskAssignee) => assignee.name)
                .join(", ") || "N/A"}
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.BlueLightTextColor}
              style={{ marginBottom: "0.5rem" }}
            >
              Updated time:
            </CustomTypography>
            <CustomTypography variant="heading6">
              {formatDate(data?.modified_date)}
            </CustomTypography>
          </Grid2>

          <Grid2 size={{ xs: 6 }}>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.BlueLightTextColor}
              style={{ marginBottom: "0.5rem" }}
            >
              Updated by:
            </CustomTypography>
            <CustomTypography variant="heading6">
              {data?.editor_name || "N/A"}
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.BlueLightTextColor}
              style={{ marginBottom: "0.5rem" }}
            >
              Product Details:
            </CustomTypography>
            <Box>
              {data?.products && data.products.length > 0 ? (
                data.products.map((product: OrderProducts, index: number) => (
                  <CustomTypography
                    key={product.id}
                    variant="heading6"
                    style={{
                      marginBottom: index < data.products!.length - 1 ? 2 : 0,
                    }}
                  >
                    {product.name}
                    {product.id && ` (${product.id})`}
                  </CustomTypography>
                ))
              ) : (
                <CustomTypography variant="heading6">N/A</CustomTypography>
              )}
            </Box>
          </Grid2>
        </Grid2>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 3,
            mt: 4,
          }}
        >
          <CustomTypography variant="heading6">Description</CustomTypography>

          <Divider
            sx={{
              flexGrow: 1,
              mt: 0.5,
              borderBottomWidth: "1px",
            }}
          />
        </Box>

        <Box
          dangerouslySetInnerHTML={{
            __html: data?.description || "No description available",
          }}
          sx={{
            color: theme.palette.text.secondary,
            fontSize: "14px",
            paddingLeft: "2px",
            lineHeight: "1.6",
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ViewTaskPopup;
